// The three-strike scaffold from the pedagogy memo.
//
// Rule: failure is never a dead end. The kid always leaves the encounter
// having said the word correctly — even if the third "attempt" is a
// scaffolded choral repeat. This is the most important pedagogical rule
// in the whole game; everything else is in service of it.

import type { Outcome } from '../speech/scoring';

export type ScaffoldStep = 0 | 1 | 2 | 3;

export interface ScaffoldAction {
  step: ScaffoldStep;       // step the kid is about to perform
  done: boolean;            // true once the encounter is resolved
  show:
    | { kind: 'first' }                                      // step 0: just the hanzi
    | { kind: 'play-audio'; replay: true }                    // step 1: replay native audio, glow
    | { kind: 'pinyin-and-stroke'; gracedHpHit: true }        // step 2: pinyin reveal + stroke order
    | { kind: 'choral'; runAway: true; reQueue: true };       // step 3: say-with-audio, monster runs
  selfDamagePct: number;    // hp lost on THIS step (graze on step 2)
}

// Decide what to do after the current attempt's outcome.
export function nextScaffoldStep(currentStep: ScaffoldStep, outcome: Outcome): ScaffoldAction {
  // critical / hit / glance all count as "passed" the encounter — no scaffold needed.
  if (outcome === 'critical' || outcome === 'hit' || outcome === 'glance') {
    return { step: currentStep, done: true, show: { kind: 'first' }, selfDamagePct: 0 };
  }

  // miss / fumble — advance the scaffold.
  if (currentStep === 0) {
    return { step: 1, done: false, show: { kind: 'play-audio', replay: true }, selfDamagePct: 0 };
  }
  if (currentStep === 1) {
    return { step: 2, done: false, show: { kind: 'pinyin-and-stroke', gracedHpHit: true }, selfDamagePct: 0.05 };
  }
  if (currentStep === 2) {
    return { step: 3, done: true, show: { kind: 'choral', runAway: true, reQueue: true }, selfDamagePct: 0.10 };
  }
  // Already at step 3; resolve.
  return { step: 3, done: true, show: { kind: 'choral', runAway: true, reQueue: true }, selfDamagePct: 0 };
}
